/** Phase 2B — Asset edit drawer + multi-kind preview.
 *
 * For copy/prompt: textarea + markdown preview toggle.
 * For image/video: inline preview.
 * Editable: name, language, tags, text_content (where applicable).
 * Permission-gated: edit only with assets.write; approve/reject with assets.approve.
 */
import { useEffect, useState } from "react";
import { api, auth } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetClose,
} from "../../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const ROLES_CAN_WRITE = new Set(["owner", "admin", "manager", "producer"]);
const ROLES_CAN_APPROVE = new Set(["owner", "admin", "approver"]);

// Tiny markdown renderer — headings, bold, italic, lists, links.
function renderMd(src) {
  if (!src) return "";
  let s = src
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code class='bg-muted px-1 rounded'>$1</code>")
    .replace(/\[(.+?)\]\((.+?)\)/g,
             "<a class='underline' href='$2' target='_blank' rel='noreferrer'>$1</a>");
  const lines = s.split("\n");
  const out = [];
  let inList = false;
  for (const ln of lines) {
    if (/^### /.test(ln)) { out.push(`<h3 class='font-semibold mt-3'>${ln.slice(4)}</h3>`); continue; }
    if (/^## /.test(ln))  { out.push(`<h2 class='font-semibold mt-3 text-lg'>${ln.slice(3)}</h2>`); continue; }
    if (/^# /.test(ln))   { out.push(`<h1 class='font-semibold mt-3 text-xl'>${ln.slice(2)}</h1>`); continue; }
    if (/^- /.test(ln)) {
      if (!inList) { out.push("<ul class='list-disc ml-5 my-2'>"); inList = true; }
      out.push(`<li>${ln.slice(2)}</li>`);
      continue;
    }
    if (inList) { out.push("</ul>"); inList = false; }
    if (ln.trim() === "") out.push("<br/>");
    else out.push(`<p>${ln}</p>`);
  }
  if (inList) out.push("</ul>");
  return out.join("");
}

export default function AssetEditDrawer({ assetId, open, onClose, onChanged }) {
  const [asset, setAsset] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const user = auth.getUser() || { role: "" };
  const canWrite = ROLES_CAN_WRITE.has(user.role);
  const canApprove = ROLES_CAN_APPROVE.has(user.role);

  useEffect(() => {
    if (!assetId || !open) { setAsset(null); setDraft(null); return; }
    api.get(`/assets/${assetId}`).then((r) => {
      setAsset(r.data);
      setDraft({
        name: r.data.name || "",
        tags: (r.data.tags || []).join(", "),
        language: r.data.language || "",
        text_content: r.data.text_content || "",
      });
    }).catch(() => toast.error("Failed to load asset"));
  }, [assetId, open]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: draft.name,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
        language: draft.language || null,
      };
      if (asset.kind === "copy" || asset.kind === "prompt") {
        body.text_content = draft.text_content;
      }
      const r = await api.put(`/assets/${asset.id}`, body);
      setAsset(r.data);
      toast.success("Saved");
      onChanged?.();
    } catch (e) {
      if (e?.response?.status !== 403) toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const action = async (suffix, body) => {
    setBusy(true);
    try {
      const r = await api.post(`/assets/${asset.id}/${suffix}`, body);
      setAsset(r.data);
      toast.success(`${suffix} successful`);
      onChanged?.();
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(`${suffix} failed`);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="asset-drawer" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="font-mono text-xs">{asset?.kind}</span>
            <span className="truncate">{asset?.name}</span>
            {asset && <Badge variant="outline">{asset.status}</Badge>}
          </SheetTitle>
        </SheetHeader>

        {!asset || !draft ? (
          <div className="text-sm text-muted-foreground mt-6">Loading…</div>
        ) : (
          <div className="mt-4 space-y-4">
            {asset.kind === "image" && asset.preview_url && (
              <img src={asset.preview_url} alt={asset.name} className="w-full rounded-md border" data-testid="drawer-preview-image" />
            )}
            {asset.kind === "video" && asset.file_url && (
              <video controls className="w-full rounded-md border" data-testid="drawer-preview-video">
                <source src={asset.file_url} />
              </video>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input data-testid="drawer-name" value={draft.name} disabled={!canWrite}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div>
                <Label>Language</Label>
                <Select value={draft.language || ""} onValueChange={(v) => setDraft({ ...draft, language: v })} disabled={!canWrite}>
                  <SelectTrigger data-testid="drawer-language"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">DE</SelectItem>
                    <SelectItem value="en">EN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Version</Label>
                <Input readOnly value={`v${asset.version}`} className="font-mono text-xs" />
              </div>
              <div className="col-span-2">
                <Label>Tags (comma separated)</Label>
                <Input data-testid="drawer-tags" value={draft.tags} disabled={!canWrite}
                       onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
              </div>
            </div>

            {(asset.kind === "copy" || asset.kind === "prompt") && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Text</Label>
                  <Button variant="ghost" size="sm" data-testid="drawer-preview-toggle"
                          onClick={() => setPreview((v) => !v)}>
                    {preview ? "Edit" : "Markdown preview"}
                  </Button>
                </div>
                {preview ? (
                  <div
                    data-testid="drawer-preview-md"
                    className="prose prose-sm max-w-none border rounded-md p-3 min-h-[160px] bg-muted/20 text-sm"
                    dangerouslySetInnerHTML={{ __html: renderMd(draft.text_content) }}
                  />
                ) : (
                  <Textarea
                    data-testid="drawer-text"
                    value={draft.text_content}
                    onChange={(e) => setDraft({ ...draft, text_content: e.target.value })}
                    rows={10}
                    disabled={!canWrite}
                  />
                )}
              </div>
            )}
          </div>
        )}

        <SheetFooter className="mt-6 flex-row flex-wrap gap-2 justify-end">
          {asset && canApprove && asset.status === "draft" && (
            <>
              <Button variant="outline" data-testid="drawer-submit-review"
                      disabled={busy} onClick={() => action("submit-review")}>Submit for review</Button>
              <Button data-testid="drawer-approve"
                      disabled={busy} onClick={() => action("approve")}>Genehmigen</Button>
            </>
          )}
          {asset && canApprove && asset.status === "draft" && (
            <Button variant="destructive" data-testid="drawer-reject" disabled={busy}
                    onClick={() => action("reject", { reason: "not_approved" })}>Ablehnen</Button>
          )}
          <SheetClose asChild>
            <Button variant="ghost" data-testid="drawer-close">Schließen</Button>
          </SheetClose>
          {canWrite && (
            <Button onClick={save} disabled={busy} data-testid="drawer-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
